// Import React so we can use hooks
import { useCallback } from "react";

// Import product view compatibility helpers
import { removeIncompatibleMockupImages } from "../utilities/PodProductViewSettingsHelpers";


// This safely upserts (update or insert) a translation object by language
export const UpsertTranslationByLanguage = ({
    // This is the existing translations array
    translations,
    // This is the chosen language e.g. "EN"
    language,
    // This is the key to update inside that translation e.g. "title"
    key,
    // This is the value for that key
    value,
}) => {

    // This ensures translations is always an array
    const safeTranslations = Array.isArray(translations) ? translations : [];

    // This normalises language to a string
    const safeLanguage = String(language || "");

    // This finds the translation index for that language
    const idx = safeTranslations.findIndex((t) => String(t?.language) === safeLanguage);

    // This checks if we already have that language
    const exists = idx !== -1;

    // This clones the translations array immutably
    const nextTranslations = [...safeTranslations];

    // This builds the updated translation object
    const nextTranslation = exists
        ? {

            // This keeps existing translation fields
            ...nextTranslations[idx],

            // This writes the new key/value
            [key]: value,

        }
        : {

            // This creates the translation object for that language
            language: safeLanguage,

            // This sets the requested key/value
            [key]: value,

        };

    // This writes it back into the array
    if (exists) {

        // This replaces the existing translation
        nextTranslations[idx] = nextTranslation;

    } else {

        // This appends a new translation
        nextTranslations.push(nextTranslation);

    }

    // This returns the updated translations array
    return nextTranslations;

};



// This safely upserts (update or insert) a roles object by role (aka exterior_front_main)
export const UpsertRolesByRole = ({
    roles,
    role,
    key,
    value,
    upsertEntireRole = false,

}) => {

    // This ensures roles is always an array
    const safeRoles = Array.isArray(roles) ? roles : [];

    // This normalises role to a string
    const safeRole = String(role || "");

    // This finds the role index for that role type
    const idx = safeRoles.findIndex((t) => String(t?.role) === safeRole);

    // This checks if we already have that role
    const exists = idx !== -1;

    // This clones the roles array immutably
    const nextRoles = [...safeRoles];

    // This builds the updated role object
    const nextRole = upsertEntireRole

        // If replacing/upserting the full role object
        ? {
            // Keep existing values if the role already exists
            ...(exists ? nextRoles[idx] : {}),

            // Apply the new full role object
            ...(value && typeof value === "object" ? value : {}),

            // Always guarantee the role value exists
            role: safeRole,
        }

        // Otherwise just update a single key
        : exists
        ? {

            // This keeps existing role fields
            ...nextRoles[idx],

            // This writes the new key/value
            [key]: value,

        }
        : {

            // This creates the role object for that role
            role: safeRole,

            // This sets the requested key/value
            [key]: value,

        };

    // This writes it back into the array
    if (exists) {

        // This replaces the existing role
        nextRoles[idx] = nextRole;

    } else {

        // This appends a new role
        nextRoles.push(nextRole);

    }
    
    // This returns the updated roles array
    return nextRoles;

};



// This hook returns a function that can update a field on a parent product
export const UseParentProductFieldUpdater = (setCurrentProducts) => {

    // This creates a stable updater function
    const updateParentProductField = useCallback(({

        // This is the configured product id
        configuredProductId = null,
        // This is the key to update
        key,
        // This is the value to store
        value,
        // This decides where to store the key ("root", "translation", "roles")
        target = "root",
        // This is the language for translation updates
        language = "EN",
        // This is the role for roles updates
        targetRole = "",
        // This decides whether to upsert the enture role object rather than just a key
        upsertEntireRole = false,
        // This decides whether incompatible generated mockup rows should be removed
        stripIncompatibleMockupImages = false,
        // This is the canonical product used for exact view compatibility checking
        canonicalProduct = null,

    } = {}) => {

        // This prevents invalid calls
        if (!setCurrentProducts || (!key && !upsertEntireRole)) {

            // This exits early safely
            return;

        }

        // This normalises configured product id for consistent comparisons
        const safeConfiguredProductId = configuredProductId ? String(configuredProductId) : "";

        // This updates products in state
        setCurrentProducts((prevDetails) => {

            // This ensures we always have an object
            const safePrevDetails = prevDetails ?? {};

            // This ensures products is always an array
            const prevProducts = Array.isArray(safePrevDetails?.products) ? safePrevDetails.products : [];

            // This finds the product index by configured product id when available
            let productIndex = -1;
            if (safeConfiguredProductId) {
                productIndex = prevProducts.findIndex((p) => String(p?.id || "") === safeConfiguredProductId || String(p?.client_configured_product_id || "") === safeConfiguredProductId);
            }

            // This checks existence
            const productExists = productIndex !== -1;

            // If no configured product id was supplied, do not fall back to sku matching
            if (!safeConfiguredProductId) {

                // This returns unchanged
                return safePrevDetails;

            }

            // If a configured product id was supplied but no product matched, do not create a ghost product
            if (!productExists) {

                // This returns unchanged
                return safePrevDetails;

            }

            // This clones products immutably
            const nextProducts = [...prevProducts];

            // This reads the existing product
            const prevProduct = nextProducts[productIndex];

            // This builds the updated product depending on target
            const nextProduct = target === "translation"
                ? {

                    // This keeps existing fields
                    ...prevProduct,

                    // This updates translations array for the chosen language
                    translations: UpsertTranslationByLanguage({
                        translations: prevProduct?.translations,
                        language: language,
                        key: key,
                        value: value,
                    }),

                } : target === "roles"
                    ? {

                        // This keeps existing fields
                        ...prevProduct,

                        // This updates roles for certain role 
                        roles: UpsertRolesByRole({
                            roles: prevProduct?.roles,
                            role: targetRole,
                            key: key,
                            value: value,
                            upsertEntireRole: upsertEntireRole,
                        }),

                    }
                         
                : {

                    // This keeps existing fields
                    ...prevProduct,

                    // This writes root key/value
                    [key]: value,

                };

            // Store the final updated product
            let finalProduct = nextProduct;

            // Remove generated mockup rows which no longer match the updated role settings
            if (stripIncompatibleMockupImages && target === "roles") {

                // Rebuild the product with only compatible image rows
                finalProduct = {
                    ...nextProduct,
                    images: removeIncompatibleMockupImages(
                        prevProduct?.images,
                        canonicalProduct,
                        nextProduct?.roles
                    ),
                };

            }

            // This writes updated product back to products array
            nextProducts[productIndex] = finalProduct;

            // This returns updated state
            return {

                // This keeps existing fields
                ...safePrevDetails,

                // This writes updated products
                products: nextProducts,

            };

        });

    }, [setCurrentProducts]);

    // This returns the updater
    return updateParentProductField;

};



// This hook returns a function that can update a field on a child product inside a parent product
export const UseChildProductFieldUpdater = (setCurrentProducts) => {

    // This creates a stable updater function
    const updateChildProductField = useCallback(({

        // This is the configured parent product id
        configuredProductId = null,
        // This is the child sku
        childSku,
        // This is the configured child product id
        childConfiguredProductId = "",
        // This is the key to update
        key,
        // This is the value to store
        value,
        // This decides where to store the key ("root", "translation", "roles")
        target = "root",
        // This is the language for translation updates
        language = "EN",
        // This is the role for roles updates
        targetRole = "",
        // This decides whether incompatible generated mockup rows should be removed
        stripIncompatibleMockupImages = false,
        // This is the canonical product used for exact view compatibility checking
        canonicalProduct = null,

    } = {}) => {

        // This prevents invalid calls
        if (!setCurrentProducts || !childSku || !key) {

            // This exits early safely
            return;

        }

        // This normalises configured product id
        const safeConfiguredProductId = configuredProductId ? String(configuredProductId) : "";

        // This normalises child configured product id
        const safeChildConfiguredProductId = childConfiguredProductId ? String(childConfiguredProductId) : "";

        // This normalises childSku
        const safeChildSku = String(childSku);

        // This updates products in state
        setCurrentProducts((prevDetails) => {

            // This ensures object
            const safePrevDetails = prevDetails ?? {};

            // This ensures products array
            const prevProducts = Array.isArray(safePrevDetails?.products) ? safePrevDetails.products : [];

            // This finds parent by configured product id when available
            let parentIndex = -1;
            if (safeConfiguredProductId) {
                parentIndex = prevProducts.findIndex((p) => String(p?.id || "") === safeConfiguredProductId || String(p?.client_configured_product_id || "") === safeConfiguredProductId);
            }

            // This checks parent exists
            const parentExists = parentIndex !== -1;

            // If no configured product id was supplied, do not fall back to sku matching
            if (!safeConfiguredProductId) {

                // This returns unchanged
                return safePrevDetails;

            }

            // If a configured product id was supplied but no parent matched, do not create a ghost parent product
            if (!parentExists) {

                // This returns unchanged
                return safePrevDetails;

            }

            // This clones products
            const nextProducts = [...prevProducts];

            // This reads the existing parent
            const prevParent = nextProducts[parentIndex];

            // This ensures children array
            const prevChildren = Array.isArray(prevParent?.children) ? prevParent.children : [];

            // This finds child by configured product id when available, otherwise by sku
            const childIndex = prevChildren.findIndex((c) => {

                // Match by configured child product id when available
                if (safeChildConfiguredProductId) {
                    return String(c?.id || "") === safeChildConfiguredProductId || String(c?.client_configured_product_id || "") === safeChildConfiguredProductId;
                }

                // Fall back to sku matching
                return String(c?.pod_product_sku) === safeChildSku;

            });

            // This checks child exists
            const childExists = childIndex !== -1;

            // If the child does not exist, do not create a ghost child
            if (!childExists) {

                // This returns unchanged
                return safePrevDetails;

            }

            // This clones children array
            const nextChildren = [...prevChildren];

            // This reads existing child
            const prevChild = nextChildren[childIndex];

            // This builds the updated child depending on target
            const nextChild = target === "translation"
                ? {

                    // This keeps existing fields
                    ...prevChild,

                    // This updates translations for chosen language
                    translations: UpsertTranslationByLanguage({
                        translations: prevChild?.translations,
                        language: language,
                        key: key,
                        value: value,
                    }),

                } 
                    : target === "roles"
                    ? {

                        // This keeps existing fields
                        ...prevChild,

                        // This updates translations for chosen language
                        roles: UpsertRolesByRole({
                            roles: prevChild?.roles,
                            role: targetRole,
                            key: key,
                            value: value,
                        }),

                    }

                : {

                    // This keeps existing fields
                    ...prevChild,

                    // This writes root key/value
                    [key]: value,

                };

            // Store the final updated child
            let finalChild = nextChild;

            // Remove generated mockup rows which no longer match the updated role settings
            if (stripIncompatibleMockupImages && target === "roles") {

                // Rebuild the child with only compatible image rows
                finalChild = {
                    ...nextChild,
                    images: removeIncompatibleMockupImages(
                        prevChild?.images,
                        canonicalProduct,
                        nextChild?.roles
                    ),
                };

            }

            // This writes child back
            nextChildren[childIndex] = finalChild;

            // This builds updated parent with children
            const nextParent = {

                // This keeps fields
                ...prevParent,

                // This writes children
                children: nextChildren,

            };

            // Replace the existing parent with its updated children
            nextProducts[parentIndex] = nextParent;

            // This returns updated state
            return {

                // This keeps existing fields
                ...safePrevDetails,

                // This writes products
                products: nextProducts,

            };

        });

    }, [setCurrentProducts]);

    // This returns the updater
    return updateChildProductField;

};